import { z } from 'zod';
import i18n from '@/shared/i18n';
import type {
  V2BookConnectionDescriptor,
  Bdkv2CredentialDescriptor,
  Bdkv2BookDescriptorConfigMap,
  Bdkv2Value,
} from '@/generated/hooks/bdk/models';
import { parseConfigMapFields, type IConfigMapFieldInfo } from './utils';

// Helper function to validate connection name with existing connection names check
export const createConnectionNameValidation = (
  existingConnectionNames: string[] = [],
) =>
  z
    .string()
    .min(
      1,
      i18n.t('integrations.addConnection.validation.connectionName.required', {
        ns: 'integrations',
      }),
    )
    .refine(name => !existingConnectionNames.includes(name), {
      message: i18n.t(
        'integrations.addConnection.validation.connectionName.unique',
        {
          ns: 'integrations',
        },
      ),
    });

const authMethodValidation = z.string().min(
  1,
  i18n.t(
    'integrations.addConnection.validation.authenticationMethod.required',
    {
      ns: 'integrations',
    },
  ),
);

// Dynamic schema creation for step 1 with existing connection names validation
export const createAuthenticationMethodStepSchema = (
  existingConnectionNames: string[] = [],
) =>
  z.object({
    connectionName: createConnectionNameValidation(existingConnectionNames),
    authMethodId: authMethodValidation,
    createSameConnection: z.boolean(),
    addProductionLater: z.boolean().optional(),
  });

export const SyncCredentialsSchema = () =>
  z.object({
    syncCredentials: z.boolean(),
  });

// Dynamic schema creation for credentials with custom error messages
export const createAuthenticationCredentialsSchema = (
  credentials: Bdkv2CredentialDescriptor[],
) => {
  const schemaFields: Record<string, z.ZodString> = {};

  credentials.forEach(credential => {
    const fieldName = credential.id || '';
    const fieldLabel = credential.label || credential.id || 'field';

    const requiredMessage = i18n.t(
      'integrations.addConnection.validation.credentialValue.required',
      { ns: 'integrations', fieldLabel },
    );
    schemaFields[fieldName] = z
      .string({ error: requiredMessage })
      .min(1, requiredMessage);
  });

  return z.object(schemaFields);
};

// Dynamic wizard schema creation with existing connection names validation
export const createAddConnectionWizardSchema = (
  existingConnectionNames: string[] = [],
  credentialDescriptors: Bdkv2CredentialDescriptor[],
) =>
  z.object({
    // Step 1 data
    connectionName: createConnectionNameValidation(existingConnectionNames),
    authMethodId: authMethodValidation,

    syncCredentials: z.boolean(),

    // Step 2 data (test credentials)
    draftConfig: createAuthenticationCredentialsSchema(credentialDescriptors),

    // Step 3 data (production credentials)
    publishedConfig: createAuthenticationCredentialsSchema(
      credentialDescriptors,
    ),
  });

// Base types for form data
export interface AuthenticationMethodStepData {
  connectionName: string;
  authMethodId: string;
  createSameConnection: boolean;
  addProductionLater?: boolean;
}

export interface AuthenticationCredentialsStepData {
  [key: string]: string;
}

export interface AddConnectionWizardData {
  connectionName: string;
  authMethodId: string;
  createSameConnection: boolean;
  addProductionLater?: boolean;
  draftConfig: AuthenticationCredentialsStepData;
  publishedConfig: AuthenticationCredentialsStepData;
  draftConnectionId?: string;
  sameConnectionId?: string;
  publishedConnectionId?: string;
  connectionType?: ConnectionType;
  remoteConnectionId?: string;
  /** Stored API-formatted config map data for deferred connection creation */
  storedConfigMapData?: Record<string, Bdkv2Value>;
  /**
   * True when the user picked "Connect Without Credentials" on an integration
   * with `connection_required === false`. Used to route them through step 2
   * to collect config_map before creating the connection, while suppressing
   * the credentials block that would otherwise render.
   */
  connectWithoutCredentials?: boolean;
}

export interface EditConnectionData {
  activeEnvironment: string;
  syncCredentials: boolean;
  draftConfig: AuthenticationCredentialsStepData;
  publishedConfig: AuthenticationCredentialsStepData;
}

// Schema factory for EditConnectionDialog
export const createEditConnectionSchema = (
  credentials: Bdkv2CredentialDescriptor[],
) =>
  z.object({
    activeEnvironment: z.string(),
    syncCredentials: z.boolean(),
    draftConfig: createAuthenticationCredentialsSchema(credentials),
    publishedConfig: createAuthenticationCredentialsSchema(credentials),
  });

/**
 * Creates a Zod schema field for a single config_map entry.
 *
 * @param field - The parsed config_map field info
 * @returns A Zod schema for the field
 */
const createConfigMapFieldSchema = (
  field: IConfigMapFieldInfo,
): z.ZodTypeAny => {
  const { fieldType, isOptional, label, enumOptions } = field;

  let schema: z.ZodTypeAny;

  switch (fieldType) {
    case 'number':
      // Accept string or number, transform to number for API
      schema = z.union([z.string(), z.number()]).refine(
        val => {
          if (val === '' || val === undefined || val === null) {
            return isOptional;
          }
          const num = typeof val === 'number' ? val : parseFloat(val);
          return !isNaN(num);
        },
        {
          message: i18n.t('integrations.configMap.validation.invalidNumber', {
            ns: 'integrations',
            fieldLabel: label,
          }),
        },
      );
      break;

    case 'boolean':
      schema = z.boolean();
      break;

    case 'enum':
      if (enumOptions && enumOptions.length > 0) {
        const enumValues = enumOptions.map(opt => opt.value);
        schema = z.enum(enumValues as [string, ...string[]]);
      } else {
        schema = z.string();
      }
      break;

    case 'text':
    default:
      schema = z.string();
  }

  // Make field optional or required based on isOptional flag
  if (isOptional) {
    // For optional fields, allow empty string
    if (
      fieldType === 'text' ||
      fieldType === 'enum' ||
      fieldType === 'number'
    ) {
      return z.union([schema, z.literal('')]).optional();
    }
    return schema.optional();
  }

  // Required field validation
  if (fieldType === 'text' || fieldType === 'enum') {
    return z.string().min(
      1,
      i18n.t('integrations.configMap.validation.required', {
        ns: 'integrations',
        fieldLabel: label,
      }),
    );
  }

  if (fieldType === 'number') {
    return z.union([z.string(), z.number()]).refine(
      val => {
        if (val === '' || val === undefined || val === null) {
          return false;
        }
        const num = typeof val === 'number' ? val : parseFloat(String(val));
        return !isNaN(num);
      },
      {
        message: i18n.t('integrations.configMap.validation.required', {
          ns: 'integrations',
          fieldLabel: label,
        }),
      },
    );
  }

  return schema;
};

/**
 * Creates a Zod schema for config_map fields based on the book's config_map descriptor.
 * Dynamically generates validation rules based on field types and optionality.
 *
 * @param configMap - The config_map descriptor from the book
 * @returns A Zod object schema for all config_map fields
 *
 * @example
 * const schema = createConfigMapSchema({
 *   company_id: { type: { optional_type: { inner: { scalar_type: 'ConceptScalarTypeNumber' } } } },
 *   environment: { type: { enum_type: { members: [{ name: 'PRODUCTION' }, { name: 'SANDBOX' }] } } }
 * });
 */
export const createConfigMapSchema = (
  configMap: Bdkv2BookDescriptorConfigMap | undefined,
) => {
  if (!configMap || Object.keys(configMap).length === 0) {
    return z.object({});
  }

  const fields = parseConfigMapFields(configMap);
  const schemaFields: Record<string, z.ZodTypeAny> = {};

  fields.forEach(field => {
    schemaFields[field.key] = createConfigMapFieldSchema(field);
  });

  return z.object(schemaFields);
};

/**
 * Config map form data type - values can be string, number, or boolean.
 */
export interface ConfigMapFormData {
  [key: string]: string | number | boolean;
}

export interface Connection {
  connectionName: string;
  bookConnections: V2BookConnectionDescriptor[];
}

export enum ConnectionType {
  Public = 'public',
  Remote = 'remote',
}

export enum EnvironmentType {
  Draft = 'Test',
  Published = 'Production',
}

export enum IntegrationsView {
  ExploreIntegrations = 'explore-integrations',
  Connections = 'connections',
}

// ConfigureActionsDialog types
export interface IConfigureActionsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  connections: V2BookConnectionDescriptor[];
  integrationName: string;
  organizationId: string;
  workspaceId: string;
  onSuccess?: () => void;
}

export interface IDiscoverablesSearchProps {
  searchTerm: string;
  onSearchChange: (value: string) => void;
}

export interface IDiscoverablesTableProps {
  isLoading: boolean;
  discoverables: Array<{ name?: string; description?: string }>;
  enabledProcedures: Set<string>;
  onToggle: (procedureName: string, enabled: boolean) => void;
  error?: Error | null;
  onRetry?: () => void;
  /**
   * Names that are saved on the connection but no longer present in the
   * current discoverables list (e.g. after a config change). Rendered as
   * disabled, visually-muted rows so the user can see what will be pruned
   * on the next Save.
   */
  orphanNames?: string[];
}

export interface IDiscoverablesTableRowProps {
  discoverable: { name?: string; description?: string };
  isEnabled: boolean;
  onToggle: (procedureName: string, enabled: boolean) => void;
  /** When true, render the row as a stale/orphan entry (disabled, muted). */
  isOrphan?: boolean;
}
